import type { DesktopGridItem } from 'shared/types/desktop-grid'

import { ensureBaseLayout, getBaseLayout, setLayoutMode } from './layoutMemory'
import { createOccupancy } from './occupancy'
import type { LayoutMode, LayoutPlacementResult, ReflowOptions } from './types'

export const SEQUENTIAL_BREAKPOINT = 768

export const resolveLayoutMode = (containerWidth: number): LayoutMode =>
  containerWidth < SEQUENTIAL_BREAKPOINT ? 'sequential' : 'desktop'

const compareByBaseOrder = (a: DesktopGridItem, b: DesktopGridItem) => {
  const baseA = getBaseLayout(a.id)
  const baseB = getBaseLayout(b.id)

  if (baseA && baseB) {
    if (baseA.order !== baseB.order) return baseA.order - baseB.order
    if (baseA.position.row !== baseB.position.row)
      return baseA.position.row - baseB.position.row
    return baseA.position.col - baseB.position.col
  }
  if (baseA) return -1
  if (baseB) return 1

  if (a.position.row !== b.position.row) return a.position.row - b.position.row
  if (a.position.col !== b.position.col) return a.position.col - b.position.col
  return a.id.localeCompare(b.id)
}

const findFirstFreePosition = (
  width: number,
  height: number,
  cols: number,
  maxRows: number,
  occupancy: ReturnType<typeof createOccupancy>
) => {
  // an item wider than the grid can never fit. Clamp the search width so
  // this loop terminates instead of scanning rows forever. The item keeps
  // its real width and just overflows until the grid is wide enough again.
  const searchWidth = Math.min(width, cols)
  for (let row = 0; row < maxRows; row += 1) {
    for (let col = 0; col <= cols - searchWidth; col += 1) {
      if (occupancy.isFree(row, col, searchWidth, height)) {
        return { row, col }
      }
    }
  }
  // safety net; a clamped slot should always exist above.
  return { row: maxRows, col: 0 }
}

type GroupMemberLayout = {
  item: DesktopGridItem
  offset: { row: number; col: number }
}

type GroupLayout = {
  members: GroupMemberLayout[]
  position: { row: number; col: number }
  size: { width: number; height: number }
  representative: DesktopGridItem
}

type LayoutUnit =
  | { kind: 'item'; item: DesktopGridItem }
  | { kind: 'group'; group: GroupLayout }

const createGroupLayout = (
  members: DesktopGridItem[],
  fitCols: number
): GroupLayout => {
  const positionedMembers = members.map(item => ({
    item,
    position: getBaseLayout(item.id)?.position ?? item.position
  }))
  const naturalTop = Math.min(
    ...positionedMembers.map(member => member.position.row)
  )
  const naturalLeft = Math.min(
    ...positionedMembers.map(member => member.position.col)
  )
  const naturalRight = Math.max(
    ...positionedMembers.map(
      member => member.position.col + member.item.size.width
    )
  )
  const naturalWidth = naturalRight - naturalLeft
  const needsCompaction = naturalWidth > fitCols
  const slotWidth = Math.max(...members.map(member => member.size.width))
  const slotHeight = Math.max(...members.map(member => member.size.height))
  const columnsPerRow = Math.max(1, Math.floor(fitCols / slotWidth))
  const compactWidth = Math.min(members.length, columnsPerRow) * slotWidth
  const compactTop = Math.max(0, naturalTop)
  const compactLeft = Math.max(0, Math.min(naturalLeft, fitCols - compactWidth))

  // A desktop group can be wider than a phone viewport (for example, five
  // 3x3 shortcuts arranged in one row). Keep the group as one unit, but wrap
  // its members into a compact local grid so no member is hidden by the
  // desktop grid's horizontal overflow boundary.
  const compactMembers = needsCompaction
    ? members.map((member, index) => ({
        item: member,
        position: {
          row: compactTop + Math.floor(index / columnsPerRow) * slotHeight,
          col: compactLeft + (index % columnsPerRow) * slotWidth
        }
      }))
    : positionedMembers

  const top = needsCompaction ? compactTop : naturalTop
  const left = needsCompaction ? compactLeft : naturalLeft
  const right = Math.max(
    ...compactMembers.map(
      member => member.position.col + member.item.size.width
    )
  )
  const bottom = Math.max(
    ...compactMembers.map(
      member => member.position.row + member.item.size.height
    )
  )
  const representative = positionedMembers.reduce((first, current) =>
    compareByBaseOrder(current.item, first.item) < 0 ? current : first
  ).item

  return {
    members: compactMembers.map(member => ({
      item: member.item,
      offset: {
        row: member.position.row - top,
        col: member.position.col - left
      }
    })),
    position: { row: top, col: left },
    size: { width: right - left, height: bottom - top },
    representative
  }
}

const compareLayoutUnits = (first: LayoutUnit, second: LayoutUnit) =>
  compareByBaseOrder(
    first.kind === 'group' ? first.group.representative : first.item,
    second.kind === 'group' ? second.group.representative : second.item
  )

export function reflowItemsForGridResize(
  items: DesktopGridItem[],
  _prevCols: number,
  _prevRows: number,
  fitCols: number,
  _rows: number,
  options: ReflowOptions = {}
): LayoutPlacementResult {
  const fitRows = Math.max(0, options.fitRows ?? _rows)
  const containerWidth = options.containerWidth ?? Infinity

  setLayoutMode(resolveLayoutMode(containerWidth))

  if (fitCols <= 0 || items.length === 0) {
    return {
      items,
      needsHorizontalScroll: false,
      needsVerticalScroll: false,
      requiredCols: Math.max(1, fitCols),
      requiredRows: Math.max(1, fitRows)
    }
  }

  const pinnedItems = items.filter(item => item.isPinned)
  const itemsById = new Map(items.map(item => [item.id, item]))
  const groupedIds = new Set<string>()
  const groupUnits: LayoutUnit[] = []

  for (const group of options.groups ?? []) {
    const members = group.memberIds
      .map(memberId => itemsById.get(memberId))
      .filter(
        (item): item is DesktopGridItem =>
          !!item && !item.isPinned && !groupedIds.has(item.id)
      )
    if (members.length === 0) continue

    members.forEach(item => groupedIds.add(item.id))
    groupUnits.push({
      kind: 'group',
      group: createGroupLayout(members, fitCols)
    })
  }

  const sortedUnits: LayoutUnit[] = [
    ...groupUnits,
    ...items
      .filter(item => !item.isPinned && !groupedIds.has(item.id))
      .map(item => ({ kind: 'item' as const, item }))
  ].sort(compareLayoutUnits)
  const units: LayoutUnit[] = [
    ...pinnedItems.map(item => ({ kind: 'item' as const, item })),
    ...sortedUnits
  ]

  // Upper-bound rows: worst case every item stacks vertically
  const stackedRows =
    items.reduce((sum, item) => sum + item.size.height, 0) + fitRows + 1
  const anchoredRows = items.reduce((max, item) => {
    const position = getBaseLayout(item.id)?.position ?? item.position
    return Math.max(max, position.row + item.size.height + fitRows + 1)
  }, fitRows)
  const maxPossibleRows = Math.max(stackedRows, anchoredRows)
  const occupancy = createOccupancy(maxPossibleRows, fitCols)

  const placements = new Map<
    string,
    { row: number; col: number; width: number; height: number }
  >()

  const isRectFree = (
    row: number,
    col: number,
    width: number,
    height: number
  ) => {
    const startRow = Math.max(0, row)
    const endRow = Math.min(maxPossibleRows, row + height)
    const startCol = Math.max(0, col)
    const endCol = Math.min(fitCols, col + width)
    if (startRow >= endRow || startCol >= endCol) return false

    for (let currentRow = startRow; currentRow < endRow; currentRow += 1) {
      for (let currentCol = startCol; currentCol < endCol; currentCol += 1) {
        if (!occupancy.isFree(currentRow, currentCol, 1, 1)) return false
      }
    }
    return true
  }

  const isGroupFootprintFree = (
    row: number,
    col: number,
    width: number,
    height: number
  ) => isRectFree(row, col, width, height) && isRectFree(row - 1, col, width, 1)

  const occupyRectClipped = (
    row: number,
    col: number,
    width: number,
    height: number
  ) => {
    const startRow = Math.max(0, row)
    const endRow = Math.min(maxPossibleRows, row + height)
    const startCol = Math.max(0, col)
    const endCol = Math.min(fitCols, col + width)
    for (let currentRow = startRow; currentRow < endRow; currentRow += 1) {
      for (let currentCol = startCol; currentCol < endCol; currentCol += 1) {
        occupancy.occupy(currentRow, currentCol, 1, 1)
      }
    }
  }

  const findFirstFreeGroupPosition = (width: number, height: number) => {
    const searchWidth = Math.min(width, fitCols)
    for (let row = 0; row < maxPossibleRows; row += 1) {
      for (let col = 0; col <= fitCols - searchWidth; col += 1) {
        if (isGroupFootprintFree(row, col, width, height)) {
          return { row, col }
        }
      }
    }
    return { row: maxPossibleRows, col: 0 }
  }

  for (const unit of units) {
    if (unit.kind === 'group') {
      const { group } = unit
      const { width, height } = group.size
      const baseCol = Math.min(group.position.col, fitCols - width)
      const preferred = {
        row: Math.max(0, group.position.row),
        col: Math.max(0, baseCol)
      }
      const position = isGroupFootprintFree(
        preferred.row,
        preferred.col,
        width,
        height
      )
        ? preferred
        : findFirstFreeGroupPosition(width, height)

      group.members.forEach(member => {
        placements.set(member.item.id, {
          row: position.row + member.offset.row,
          col: position.col + member.offset.col,
          width: member.item.size.width,
          height: member.item.size.height
        })
      })

      // Reserve the complete group rectangle and its title row. This keeps
      // ungrouped shortcuts from filling holes inside a visual group.
      occupyRectClipped(position.row, position.col, width, height)
      occupyRectClipped(position.row - 1, position.col, width, 1)
      continue
    }

    const { item } = unit
    const { width, height } = item.size
    // occupy the clamped width (see findFirstFreePosition) but keep the
    // real width in the placement.
    const occupyWidth = Math.min(width, fitCols)

    if (item.isPinned) {
      // Every pinned item keeps its declared anchor. Clamp only when the grid
      // is too narrow to represent that column after a resize.
      const pos = {
        row: Math.max(0, item.position.row),
        col: Math.max(0, Math.min(item.position.col, fitCols - width))
      }
      occupancy.occupy(pos.row, pos.col, occupyWidth, height)
      placements.set(item.id, { ...pos, width, height })
      continue
    }

    const base = getBaseLayout(item.id)

    if (!base) {
      // No saved position for this mode — pack sequentially
      const pos = findFirstFreePosition(
        width,
        height,
        fitCols,
        maxPossibleRows,
        occupancy
      )
      occupancy.occupy(pos.row, pos.col, occupyWidth, height)
      placements.set(item.id, {
        row: pos.row,
        col: pos.col,
        width,
        height
      })
      continue
    }

    const baseCol = Math.min(base.position.col, fitCols - width)
    const baseRow = base.position.row
    const clampedCol = Math.max(0, baseCol)
    const clampedRow = Math.max(0, baseRow)

    if (
      clampedCol + width <= fitCols &&
      occupancy.isFree(clampedRow, clampedCol, width, height)
    ) {
      occupancy.occupy(clampedRow, clampedCol, width, height)
      placements.set(item.id, {
        row: clampedRow,
        col: clampedCol,
        width,
        height
      })
    } else {
      const pos = findFirstFreePosition(
        width,
        height,
        fitCols,
        maxPossibleRows,
        occupancy
      )
      occupancy.occupy(pos.row, pos.col, occupyWidth, height)
      placements.set(item.id, {
        row: pos.row,
        col: pos.col,
        width,
        height
      })
    }
  }

  let requiredRows = 0
  placements.forEach(p => {
    requiredRows = Math.max(requiredRows, p.row + p.height)
  })
  requiredRows = Math.max(requiredRows, 1)

  const needsVerticalScroll = fitRows > 0 && requiredRows > fitRows
  const displayRows = needsVerticalScroll ? requiredRows : Math.max(fitRows, 1)

  const result = items.map(item => {
    const p = placements.get(item.id)
    if (!p) return item
    return {
      ...item,
      position: { row: p.row, col: p.col },
      size: { width: p.width, height: p.height }
    }
  })

  // Seed the current mode's store with computed positions so that
  // re-entering this mode restores them.
  ensureBaseLayout(result, fitCols, displayRows)

  return {
    items: result,
    needsHorizontalScroll: false,
    needsVerticalScroll,
    requiredCols: fitCols,
    requiredRows: displayRows
  }
}

export {
  clearLayoutMemory,
  clearNonProfileMemory,
  ensureBaseLayout,
  updateBaseLayout
} from './layoutMemory'
